/**
 * Split script loader.
 */

const GLOBAL_KEY = 'CustomizerWidget';
const CORE_FILENAME = 'customizerwidget.core.js';
const LOADER_FILENAME_PATTERN = /customizerwidget(\.umd|\.core)?\.js([?#]|$)/i;

// Since everything is merged together in the dev build, we do not need to load the core separately.
const isDevelopment = import.meta.env.DEV;

const selfScript = document.currentScript;

let loadPromise = null;
let loaderApi = null;

/**
 * Find the current script location, aka where we are loading the loader from.
 */
function findLoaderSrc() {
    if (selfScript && selfScript.src) {
        return selfScript.src;
    }

    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const match = scripts.find((script) => LOADER_FILENAME_PATTERN.test(script.src));

    return match ? match.src : null;
}

/**
 * Based on the loader location, figure out where the script is.
 */
function resolveCoreSrc(options) {
    if (isDevelopment) {
        // Just in case of opening demo.html while in development server mode.
        return '/src/start.js';
    }

    if (options.coreSrc) {
        return options.coreSrc;
    }

    // eslint-disable-next-line no-underscore-dangle
    const override = window.__CustomizerWidgetCoreSrc;

    if (override) {
        return override;
    }

    if (selfScript) {
        const attribute = selfScript.getAttribute('data-core-src');

        if (attribute) {
            return attribute;
        }
    }

    const loaderSrc = findLoaderSrc();

    if (loaderSrc) {
        return loaderSrc.slice(0, loaderSrc.lastIndexOf('/') + 1) + CORE_FILENAME;
    }

    return CORE_FILENAME;
}

/**
 * True once the core bundle has replaced our global with its own.
 */
function isCoreApi(api) {
    // eslint-disable-next-line no-use-before-define
    return api != null && api !== loaderApi && typeof api.createWidget === 'function' && api.createWidget !== createWidget;
}

function injectCore(options) {
    return new Promise((resolve, reject) => {
        const src = resolveCoreSrc(options);
        const script = document.createElement('script');

        script.src = src;
        script.async = true;

        if (isDevelopment) {
            script.type = 'module';
        }

        if (selfScript) {
            const crossorigin = selfScript.getAttribute('crossorigin');

            if (crossorigin !== null) {
                script.setAttribute('crossorigin', crossorigin);
            }
        }

        script.addEventListener('load', () => {
            const api = window[GLOBAL_KEY];

            if (isCoreApi(api)) {
                resolve(api.createWidget);
            } else {
                reject(new Error(`[CustomizerWidget] failed to load core from ${src}`));
            }
        });

        script.addEventListener('error', () => {
            reject(new Error(`[CustomizerWidget] failed to load core from ${src}`));
        });

        (document.head || document.documentElement).appendChild(script);
    });
}

function loadCore(options = {}) {
    if (!loadPromise) {
        const existing = window[GLOBAL_KEY];

        if (isCoreApi(existing)) {
            loadPromise = Promise.resolve(existing.createWidget);
        } else {
            loadPromise = injectCore(options).catch((error) => {
                loadPromise = null;

                throw error;
            });
        }
    }

    return loadPromise;
}

function createWidget(options = {}) {
    let handle = null;
    let closeRequested = false;

    const ready = loadCore(options).then((coreCreateWidget) => {
        handle = coreCreateWidget(options);

        if (closeRequested) {
            handle.close();
        }

        return handle;
    });

    ready.catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
    });

    return {
        close() {
            if (handle) {
                handle.close();
            } else {
                closeRequested = true;
            }
        },

        saveRecipe() {
            return handle ? handle.saveRecipe() : false;
        },
    };
}

/**
 * Allow to preload the widget.
 */
function preload() {
    return loadCore();
}

const installed = window[GLOBAL_KEY];

if (!installed || typeof installed.createWidget !== 'function') {
    loaderApi = {
        createWidget,
        preload,
        isLoader: true,
    };

    window[GLOBAL_KEY] = loaderApi;
}
